import { useCallback, useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { translateError, useI18n, type TranslationValues } from './i18n';
import { LanguageSelector } from './LanguageSelector';

export function ErrorMessage({ error }: { error: unknown }) {
  const { language } = useI18n();
  if (!error) return null;
  return <div className="error-message" role="alert">{translateError(error, language)}</div>;
}
export function Busy({ label = 'Wird geladen …' }: { label?: string }) {
  const { t } = useI18n();
  return <div className="busy" role="status" aria-live="polite"><span className="loading-spinner" aria-hidden="true" /><span>{t(label)}</span></div>;
}
/** Shared AG Grid loadingOverlayComponent; set the grid's loading prop explicitly. */
export function GridLoading({ label = 'Daten werden geladen …' }: { label?: string }) {
  return <div className="grid-loading"><Busy label={label} /></div>;
}
export function Toolbar({ children }: { children: ReactNode }) { return <div className="toolbar">{children}</div>; }
export function Field({ label, children, error }: { label: string; children: ReactNode; error?: string }) {
  const { t } = useI18n();
  return <label className="field"><span className="field-label">{t(label)}</span>{children}{error && <span className="field-error">{t(error)}</span>}</label>;
}

const modalStack: symbol[] = [];
export function Modal({ title, children, onClose, footer, initialFocusRef }: {
  title: string; children: ReactNode; onClose: () => void; footer?: ReactNode; initialFocusRef?: RefObject<HTMLElement | null>;
}) {
  const { t } = useI18n();
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const close = useRef(onClose); close.current = onClose;
  useEffect(() => {
    const token = Symbol(); modalStack.push(token);
    const previous = document.activeElement as HTMLElement | null;
    const focusable = () => Array.from(root.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]') ?? []).filter(el => el.getClientRects().length > 0);
    (initialFocusRef?.current ?? root.current?.querySelector<HTMLElement>('.modal-body input:not(:disabled), .modal-body select:not(:disabled), .modal-body textarea:not(:disabled), .modal-body button:not(:disabled)') ?? focusable()[0] ?? root.current)?.focus();
    const keyboard = (event: KeyboardEvent) => {
      if (modalStack.at(-1) !== token || !root.current?.getClientRects().length) return;
      if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); close.current(); }
      if (event.key === 'Tab') {
        const items = focusable(); const first = items[0]; const last = items.at(-1);
        if (!first) { event.preventDefault(); root.current?.focus(); }
        else if (event.shiftKey && (document.activeElement === first || !root.current?.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !root.current?.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', keyboard, true);
    return () => { modalStack.splice(modalStack.indexOf(token), 1); document.removeEventListener('keydown', keyboard, true); previous?.focus(); };
  }, [initialFocusRef]);
  return createPortal(<div className="modal-backdrop"><div className="modal" role="dialog" aria-modal="true" aria-labelledby={id} ref={root} tabIndex={-1}>
    <header className="modal-title"><strong id={id}>{t(title)}</strong><div className="modal-actions"><LanguageSelector /><button aria-label={t('{title} schließen', { title: t(title) })} onClick={onClose}>×</button></div></header>
    <div className="modal-body">{children}</div>{footer && <footer className="modal-footer">{footer}</footer>}
  </div></div>, document.getElementById('shell-dialogs') ?? document.body);
}

export function ConfirmDialog({ title, message, messageParameters, confirmLabel = 'Verwerfen', onConfirm, onCancel }: {
  title: string; message: string; messageParameters?: TranslationValues; confirmLabel?: string; onConfirm: () => void; onCancel: () => void;
}) {
  const { t } = useI18n();
  const cancelRef = useRef<HTMLButtonElement>(null);
  return <Modal title={title} onClose={onCancel} initialFocusRef={cancelRef} footer={<>
    <button ref={cancelRef} onClick={onCancel}>{t('Abbrechen')}</button>
    <button onClick={onConfirm}>{t(confirmLabel)}</button>
  </>}><p>{t(message, messageParameters)}</p></Modal>;
}

/** Render confirmation in the caller's JSX and await confirm before the action. */
export function useConfirmation(): { confirm: (message: string, title?: string, messageParameters?: TranslationValues) => Promise<boolean>; confirmation: ReactNode } {
  const [request, setRequest] = useState<{ id: number; message: string; title: string; messageParameters?: TranslationValues }>();
  const sequence = useRef(0);
  const mounted = useRef(true);
  const pending = useRef<{ id: number; resolve: (accepted: boolean) => void } | undefined>(undefined);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      const previous = pending.current;
      pending.current = undefined;
      previous?.resolve(false);
    };
  }, []);
  const confirm = useCallback((message: string, title = 'Bestätigung', messageParameters?: TranslationValues): Promise<boolean> => {
    if (!mounted.current) return Promise.resolve(false);
    const previous = pending.current;
    pending.current = undefined;
    previous?.resolve(false);
    const id = ++sequence.current;
    return new Promise<boolean>(resolve => {
      pending.current = { id, resolve };
      setRequest({ id, message, title, messageParameters });
    });
  }, []);
  const settle = useCallback((id: number, accepted: boolean) => {
    if (pending.current?.id !== id) return;
    const current = pending.current;
    pending.current = undefined;
    setRequest(undefined);
    current.resolve(accepted);
  }, []);
  return {
    confirm,
    confirmation: request ? <ConfirmDialog key={request.id} title={request.title} message={request.message} messageParameters={request.messageParameters} confirmLabel="Bestätigen"
      onConfirm={() => settle(request.id, true)} onCancel={() => settle(request.id, false)} /> : null
  };
}

export type TableColumn<T> = { key: string; label: string; render?: (row: T) => ReactNode; width?: string | number; align?: 'left' | 'right' | 'center' };
export function DataTable<T>({ rows, columns, getRowKey, onActivate, selectedKey, onSelect, emptyText = 'Keine Einträge.', loading = false, loadingLabel }: {
  rows: T[]; columns: TableColumn<T>[]; getRowKey: (row: T) => string | number;
  onActivate?: (row: T) => void; selectedKey?: string | number; onSelect?: (row: T) => void; emptyText?: string; loading?: boolean; loadingLabel?: string;
}) {
  const { t } = useI18n();
  return <div className="data-table-scroll" aria-busy={loading} style={{ overflow: 'hidden' }}
    onPointerDownCapture={event => { if (loading) { event.preventDefault(); event.stopPropagation(); } }}
    onClickCapture={event => { if (loading) { event.preventDefault(); event.stopPropagation(); } }}
    onDoubleClickCapture={event => { if (loading) { event.preventDefault(); event.stopPropagation(); } }}
    onKeyDownCapture={event => { if (loading && ['Enter', ' ', 'Spacebar'].includes(event.key)) { event.preventDefault(); event.stopPropagation(); } }}
    onKeyUpCapture={event => { if (loading && ['Enter', ' ', 'Spacebar'].includes(event.key)) { event.preventDefault(); event.stopPropagation(); } }}>
    {loading && <div className="data-table-loading"><GridLoading label={loadingLabel} /></div>}<div className="data-table-viewport"><table className="data-table"><thead><tr>{columns.map(c => <th key={c.key} style={{ width: c.width, textAlign: c.align }}>{t(c.label)}</th>)}</tr></thead>
    <tbody>{rows.map((row, index) => <tr key={getRowKey(row)} tabIndex={0} aria-selected={selectedKey === getRowKey(row)}
      onClick={() => !loading && onSelect?.(row)} onDoubleClick={() => !loading && onActivate?.(row)} onFocus={() => !loading && onSelect?.(row)}
      onKeyDown={event => {
        if (loading) { if (['Enter', 'ArrowDown', 'ArrowUp'].includes(event.key)) event.preventDefault(); return; }
        if (event.key === 'Enter') { event.preventDefault(); onActivate?.(row); }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault(); const next = Math.max(0, Math.min(rows.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)));
          (event.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
        }
      }}>{columns.map(c => <td key={c.key} style={{ textAlign: c.align }}>{c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '')}</td>)}</tr>)}
      {!rows.length && <tr><td colSpan={columns.length} className="empty-state">{loading ? '\u00a0' : t(emptyText)}</td></tr>}
    </tbody></table></div></div>;
}
